import React, { useCallback, useMemo } from 'react'
import { Table, Button, Row, Col } from 'react-bootstrap'
import { BillingResult } from '../../../domain/models/pages/BillingGuideLookupPage/BillingGuideLookupModel'
import text from '../../../_constants/text'
import icons from '../../../_constants/icons'
import Icon from '../../../components/atoms/Icon'
import { generatePrintTemplate, escapeHtml } from './printTemplate'

interface BillingResultsTableProps {
  results: BillingResult[]
  sourceTimestamp?: string
}

const PRINT_SETUP_DELAY = 250
const PRINT_CLEANUP_TIMEOUT = 60000

const BillingResultsTable: React.FC<BillingResultsTableProps> = ({ results, sourceTimestamp }) => {
  if (!results || !Array.isArray(results) || results.length === 0) {
    return null
  }

  const formatTimestamp = useCallback((timestamp?: string): string => {
    if (!timestamp) return ''
    try {
      const date = new Date(timestamp)
      if (isNaN(date.getTime())) return ''
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear()
      return `${day}/${month}/${year}`
    } catch {
      return ''
    }
  }, [])

  const formatGapValue = useCallback((value: string): string => {
    // Special case for null or empty string -> "N/A"
    if (!value || value === 'null' || value === '') return 'N/A'

    // Special case for "BB" or "0" -> Display as "Bulk Billed"
    if (value === 'BB' || value === '0') return 'Bulk Billed'

    // Special case for "POA" -> return as is (don't attempt to parse)
    if (value === 'POA') return value

    // Prefix with "F" -> Add "From " before price
    if (value.startsWith('F')) {
      const numValue = parseFloat(value.substring(1))
      if (!isNaN(numValue)) {
        return `From $${numValue.toFixed(2)}`
      }
    }

    const numValue = parseFloat(value)
    if (!isNaN(numValue)) {
      return `$${numValue.toFixed(2)}`
    }

    console.warn(`Unexpected gap value format: ${value}`)
    return value
  }, [])

  const formattedTimestamp = useMemo(() => formatTimestamp(sourceTimestamp), [formatTimestamp, sourceTimestamp])

  const generateTableRows = useCallback((): string => {
    return results
      .map(
        (result) => `
        <tr>
          <td>${escapeHtml(result.location)}</td>
          <td>${escapeHtml(result.procedure)}</td>
          <td>${escapeHtml(formatGapValue(result.pensionerGap))}</td>
          <td>${escapeHtml(formatGapValue(result.privateGap))}</td>
        </tr>
      `,
      )
      .join('')
  }, [results, formatGapValue])

  const getPrintTemplate = useCallback((): string => {
    return generatePrintTemplate({
      title: text.BILLING_GUIDE_PRINT_TITLE,
      headerImageSrc: '/Header_Print_Billing_Guide.jpg',
      headerImageAlt: 'I-MED Radiology Network',
      timestamp: formattedTimestamp,
      timestampLabel: formattedTimestamp ? text.BILLING_GUIDE_RESULTS_UPDATED : undefined,
      tableHeaders: [
        text.BILLING_GUIDE_TABLE_LOCATION,
        text.BILLING_GUIDE_TABLE_PROCEDURE,
        text.BILLING_GUIDE_TABLE_PENSIONER_GAP,
        text.BILLING_GUIDE_TABLE_PRIVATE_GAP,
      ],
      tableRows: generateTableRows(),
    })
  }, [formattedTimestamp, generateTableRows])

  const handlePrint = useCallback(() => {
    let printFrame: HTMLIFrameElement | null = null

    try {
      printFrame = document.createElement('iframe')
      printFrame.style.display = 'none'
      printFrame.setAttribute('aria-hidden', 'true')
      document.body.appendChild(printFrame)

      const htmlContent = getPrintTemplate()
      printFrame.srcdoc = htmlContent

      printFrame.onload = () => {
        const setupPrint = () => {
          if (printFrame?.contentWindow) {
            try {
              printFrame.contentWindow.print()

              const cleanup = () => {
                if (printFrame && document.body.contains(printFrame)) {
                  document.body.removeChild(printFrame)
                }
              }

              printFrame.contentWindow.onafterprint = cleanup
              setTimeout(cleanup, PRINT_CLEANUP_TIMEOUT)
            } catch (error) {
              console.error('Print error:', error)
              if (printFrame && document.body.contains(printFrame)) {
                document.body.removeChild(printFrame)
              }
            }
          }
        }

        setTimeout(setupPrint, PRINT_SETUP_DELAY)
      }

      printFrame.onerror = () => {
        console.error('Failed to load print content')
        if (printFrame && document.body.contains(printFrame)) {
          document.body.removeChild(printFrame)
        }
      }
    } catch (error) {
      console.error('Failed to create print frame:', error)
      if (printFrame && document.body.contains(printFrame)) {
        document.body.removeChild(printFrame)
      }
    }
  }, [getPrintTemplate])

  return (
    <>
      <Row className="mb-2 align-items-center">
        <Col xs={12} md={6}>
          {formattedTimestamp && (
            <small className="text-muted">
              {text.BILLING_GUIDE_RESULTS_UPDATED}
              {': '}
              {formattedTimestamp}
            </small>
          )}
        </Col>
        <Col xs={12} md={6} className="text-md-end mt-2 mt-md-0">
          <Button variant="primary" size="sm" onClick={handlePrint} aria-label={text.PRINT}>
            <Icon name={icons.PRINT} className="me-2" aria-hidden="true" />
            {text.PRINT}
          </Button>
        </Col>
      </Row>

      <Table responsive bordered hover className="bg-white table-striped mb-0" aria-label="Billing guide results table">
        <thead>
          <tr>
            <th scope="col">{text.BILLING_GUIDE_TABLE_LOCATION}</th>
            <th scope="col">{text.BILLING_GUIDE_TABLE_PROCEDURE}</th>
            <th scope="col">{text.BILLING_GUIDE_TABLE_PENSIONER_GAP}</th>
            <th scope="col">{text.BILLING_GUIDE_TABLE_PRIVATE_GAP}</th>
          </tr>
        </thead>
        <tbody>
          {results.map((result, index) => (
            <tr key={`${result.location}-${result.procedure}-${index}`}>
              <td>{result.location}</td>
              <td>{result.procedure}</td>
              <td>{formatGapValue(result.pensionerGap)}</td>
              <td>{formatGapValue(result.privateGap)}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  )
}

export default BillingResultsTable
